import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { ChevronDown, Palette, Rocket, Bot, Menu, X } from "lucide-react";
import MagneticButton from "@/components/MagneticButton";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services", hasMega: true },
  { label: "Industries", to: "/industries" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Products", to: "/products" },
  { label: "AI Lab", to: "/ai-lab" },
  { label: "Blog", to: "/blog" },
];

const megaCols = [
  {
    title: "Brand",
    icon: Palette,
    color: "from-neon-orange to-neon-magenta",
    items: ["Brand Strategy", "Identity & Logo", "Creative Direction", "Content Strategy", "UI/UX Design", "Website Design"],
  },
  {
    title: "Growth",
    icon: Rocket,
    color: "from-neon-magenta to-neon-purple",
    items: ["SEO & GEO", "Performance Marketing", "Lead Generation", "Conversion Optimization", "Growth Campaigns", "Analytics"],
  },
  {
    title: "Automation",
    icon: Bot,
    color: "from-neon-cyan to-neon-blue",
    items: ["CRM Systems", "AI Agents", "Chatbots", "Workflow Automation", "Sales Automation", "Custom AI Systems"],
  },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setMegaOpen(false); }, [location.pathname]);

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -32, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "py-3" : "py-5"}`}
      onMouseLeave={() => setMegaOpen(false)}
    >
      <div className="container-x">
        <div className={`flex items-center justify-between rounded-full px-4 sm:px-6 py-2.5 transition-all duration-300 ${scrolled ? "glass-strong shadow-[0_8px_40px_rgba(0,240,255,0.08)]" : "bg-transparent"}`}>
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <span className="relative w-8 h-8 rounded-lg overflow-hidden">
              <span className="absolute inset-0 bg-gradient-to-tr from-neon-magenta via-neon-orange to-neon-cyan" />
              <span className="absolute inset-[2px] rounded-md bg-bg-0 flex items-center justify-center font-mono font-extrabold text-[12px] text-white">NW</span>
            </span>
            <span className="font-semibold tracking-tight text-[15px]">
              Novarise <span className="text-white/50">Works</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV.map((l) => (
              <div
                key={l.to}
                className="relative"
                onMouseEnter={() => l.hasMega ? setMegaOpen(true) : setMegaOpen(false)}
              >
                <NavLink
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-3 py-1.5 text-[13.5px] transition-colors rounded-full inline-flex items-center gap-1 ${
                      isActive ? "text-white bg-white/[0.06]" : "text-white/65 hover:text-white hover:bg-white/[0.04]"
                    }`
                  }
                >
                  {l.label}
                  {l.hasMega && <ChevronDown size={13} className="opacity-60" />}
                </NavLink>
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <MagneticButton
              data-testid="nav-book-call"
              onClick={() => navigate("/contact")}
              className="text-[13px] hidden sm:inline-flex"
              tone="cyan"
            >
              Book Strategy Call
            </MagneticButton>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden w-10 h-10 rounded-full glass flex items-center justify-center"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mega menu */}
        <AnimatePresence>
          {megaOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 right-0 mt-3 px-6"
              onMouseEnter={() => setMegaOpen(true)}
            >
              <div className="max-w-5xl mx-auto glass-strong rounded-2xl p-6 grid grid-cols-3 gap-6">
                {megaCols.map((col) => {
                  const Icon = col.icon;
                  return (
                    <div key={col.title}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`w-8 h-8 rounded-lg bg-gradient-to-br ${col.color} flex items-center justify-center`}>
                          <Icon size={15} />
                        </span>
                        <span className="font-semibold text-sm">{col.title}</span>
                      </div>
                      <ul className="space-y-1.5">
                        {col.items.map((it) => (
                          <li key={it}>
                            <Link to="/services" className="text-[13px] text-white/65 hover:text-white block py-0.5">
                              {it}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile drawer */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="lg:hidden mt-3 overflow-hidden"
            >
              <div className="glass-strong rounded-2xl p-4 space-y-1">
                {NAV.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    className={({ isActive }) =>
                      `block px-4 py-3 rounded-xl text-sm ${isActive ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <MagneticButton tone="warm" className="w-full mt-2 justify-center" onClick={() => navigate("/contact")}>
                  Book Strategy Call
                </MagneticButton>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
